import React, { useState } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { money as fmtMoney } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calculator } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export const AmortizationCalculator = () => {
  const { t } = useI18n();
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(12);
  const [term, setTerm] = useState(24);
  const [currency, setCurrency] = useState("DOP");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const money = (n) => fmtMoney(n, currency);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/amortization", {
        user_id: "calc",
        principal: Number(amount),
        annual_rate: Number(rate),
        term_months: Number(term),
      });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="amortization-calculator">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-lg bg-emerald-800 flex items-center justify-center">
            <Calculator size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-emerald-900">{t("calc_title")}</h3>
            <p className="text-sm text-slate-500">{t("calc_sub")}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <Label className="text-emerald-900 font-semibold">{t("amount")}</Label>
              <span className="font-mono-num text-emerald-700 font-bold">{money(amount)}</span>
            </div>
            <Slider data-testid="calc-amount-slider" value={[amount]} min={500} max={200000} step={500} onValueChange={(v) => setAmount(v[0])} />
            <Input data-testid="calc-amount-input" type="number" className="mt-2" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>

          <div>
            <div className="flex justify-between items-baseline mb-2">
              <Label className="text-emerald-900 font-semibold">{t("rate")}</Label>
              <span className="font-mono-num text-emerald-700 font-bold">{rate}%</span>
            </div>
            <Slider data-testid="calc-rate-slider" value={[rate]} min={1} max={40} step={0.5} onValueChange={(v) => setRate(v[0])} />
          </div>

          <div>
            <div className="flex justify-between items-baseline mb-2">
              <Label className="text-emerald-900 font-semibold">{t("term")}</Label>
              <span className="font-mono-num text-emerald-700 font-bold">{term}</span>
            </div>
            <Slider data-testid="calc-term-slider" value={[term]} min={3} max={360} step={1} onValueChange={(v) => setTerm(v[0])} />
          </div>

          <div>
            <Label className="text-emerald-900 font-semibold mb-2 block">{t("currency")}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="calc-currency-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DOP" data-testid="calc-currency-dop">{t("dop")}</SelectItem>
                <SelectItem value="USD" data-testid="calc-currency-usd">{t("usd")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button data-testid="calc-submit-btn" onClick={calculate} disabled={loading} className="w-full bg-emerald-800 hover:bg-emerald-900 h-11 text-base">
            {loading ? t("loading") : t("calculate")}
          </Button>
        </div>
      </div>

      <div className="bg-emerald-900 grain rounded-xl p-6 sm:p-8 text-white flex flex-col">
        {result ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col h-full">
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Stat label={t("monthly_payment")} value={money(result.monthly_payment)} big />
              <Stat label={t("total_interest")} value={money(result.total_interest)} />
              <Stat label={t("total_payment")} value={money(result.total_payment)} />
            </div>
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-2">{t("schedule")}</p>
            <div className="flex-1 overflow-y-auto no-scrollbar rounded-lg bg-emerald-950/40 border border-emerald-700/40 max-h-72">
              <table className="w-full text-xs font-mono-num" data-testid="calc-schedule-table">
                <thead className="sticky top-0 bg-emerald-950 text-emerald-300">
                  <tr>
                    <th className="px-2 py-2 text-left">{t("period")}</th>
                    <th className="px-2 py-2 text-right">{t("payment")}</th>
                    <th className="px-2 py-2 text-right">{t("principal")}</th>
                    <th className="px-2 py-2 text-right">{t("interest")}</th>
                    <th className="px-2 py-2 text-right">{t("balance")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.schedule.map((row) => (
                    <tr key={row.period} className="border-t border-emerald-800/40">
                      <td className="px-2 py-1.5 text-emerald-200">{row.period}</td>
                      <td className="px-2 py-1.5 text-right">{money(row.payment)}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-300">{money(row.principal)}</td>
                      <td className="px-2 py-1.5 text-right text-amber-300">{money(row.interest)}</td>
                      <td className="px-2 py-1.5 text-right">{money(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-emerald-200/70 min-h-[300px]">
            <Calculator size={56} weight="thin" className="mb-4" />
            <p className="max-w-xs">{t("calc_sub")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, big }) => (
  <div className="rounded-lg bg-emerald-800/50 border border-emerald-600/30 p-3">
    <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">{label}</p>
    <p className={`font-mono-num font-bold ${big ? "text-xl sm:text-2xl text-mint" : "text-sm sm:text-base"} text-white break-words`}>{value}</p>
  </div>
);
